import { theme as antTheme } from 'antd';
import type { ThemeConfig } from 'antd';

export type PaletteKey = 'default' | 'lime' | 'slip' | 'indigo' | 'sunset' | 'mono' | 'rose';

export interface PaletteTokens {
  accent: string;
  accentInk: string;
  bg: string;
  bg2: string;
  bg3: string;
  bg4: string;
  text: string;
  text2: string;
  text3: string;
  border: string;
  border2: string;
  sidebarFade: string;
  sidebarFadeEnd: string;
  green: string;
  amber: string;
  red: string;
  blue: string;
  purple: string;
}

export const PALETTES: Record<PaletteKey, { dark: PaletteTokens; light: PaletteTokens }> = {
  default: {
    dark: {
      accent: '#5599FF', accentInk: '#000D20',
      bg: '#08101A', bg2: '#0E1826', bg3: '#141F30', bg4: '#1A263C',
      text: '#D8E8F8', text2: '#8AACCC', text3: '#4E6E8E',
      border: '#1A2E48', border2: '#223858',
      sidebarFade: 'rgba(8,16,26,0.85)', sidebarFadeEnd: '#050C14',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#1A6BF4', accentInk: '#FFFFFF',
      bg: '#EFF5FF', bg2: '#F7FBFF', bg3: '#E2EDFF', bg4: '#D4E4FE',
      text: '#0A1628', text2: '#2A5090', text3: '#5878B0',
      border: '#BDD0F4', border2: '#D2E2FF',
      sidebarFade: 'rgba(239,245,255,0.85)', sidebarFadeEnd: '#EFF5FF',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  lime: {
    dark: {
      accent: '#B5F23A', accentInk: '#0C100B',
      // Backgrounds — noticeably green-tinted
      bg: '#0D100B', bg2: '#121814', bg3: '#181E18', bg4: '#1E261E',
      // Text — subtle green tint
      text: '#DEEbD6', text2: '#92A08A', text3: '#586060',
      border: '#243024', border2: '#2E3C2C',
      sidebarFade: 'rgba(13,16,11,0.85)', sidebarFadeEnd: '#090c08',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#5A9E00', accentInk: '#FFFFFF',
      bg: '#EFF2E8', bg2: '#F8FAF4', bg3: '#E6EBE0', bg4: '#DBE3D4',
      text: '#18201A', text2: '#435040', text3: '#7A8A78',
      border: '#C8D2C2', border2: '#D8E0D0',
      sidebarFade: 'rgba(239,242,232,0.85)', sidebarFadeEnd: '#EFF2E8',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  slip: {
    dark: {
      accent: '#F59E0B', accentInk: '#0A0800',
      // Backgrounds — warm amber-tinted
      bg: '#110F08', bg2: '#1A1608', bg3: '#241E0C', bg4: '#2C2612',
      // Text — warm amber tint
      text: '#F0E8CC', text2: '#A09678', text3: '#6E6244',
      border: '#342C14', border2: '#3E3618',
      sidebarFade: 'rgba(17,15,8,0.85)', sidebarFadeEnd: '#0c0b05',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#B45309', accentInk: '#FFFFFF',
      bg: '#F5F0E4', bg2: '#FDFAF0', bg3: '#EDE6D0', bg4: '#E2D8BC',
      text: '#1C1808', text2: '#504230', text3: '#8A7850',
      border: '#D4C8A0', border2: '#E2D8BC',
      sidebarFade: 'rgba(245,240,228,0.85)', sidebarFadeEnd: '#F5F0E4',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  indigo: {
    dark: {
      accent: '#818CF8', accentInk: '#08091A',
      // Backgrounds — clearly blue-violet tinted
      bg: '#090B18', bg2: '#0E1020', bg3: '#141628', bg4: '#1A1C32',
      // Text — cool blue tint
      text: '#DADDF0', text2: '#8C92B4', text3: '#565A7E',
      border: '#222444', border2: '#2C2E52',
      sidebarFade: 'rgba(9,11,24,0.85)', sidebarFadeEnd: '#060712',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#4F46E5', accentInk: '#FFFFFF',
      bg: '#EEF0FA', bg2: '#F6F7FF', bg3: '#E4E6F8', bg4: '#D8DBF4',
      text: '#14163A', text2: '#3E42A0', text3: '#7476A8',
      border: '#C4C8F0', border2: '#D8DCF8',
      sidebarFade: 'rgba(238,240,250,0.85)', sidebarFadeEnd: '#EEF0FA',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  sunset: {
    dark: {
      accent: '#FB923C', accentInk: '#110800',
      // Backgrounds — warm orange-tinted
      bg: '#140E08', bg2: '#1E1208', bg3: '#281A0C', bg4: '#322012',
      // Text — warm orange tint
      text: '#EDDDD0', text2: '#A28878', text3: '#6E5448',
      border: '#382218', border2: '#442C1E',
      sidebarFade: 'rgba(20,14,8,0.85)', sidebarFadeEnd: '#0e0905',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#EA580C', accentInk: '#FFFFFF',
      bg: '#F6EDE4', bg2: '#FEF6EE', bg3: '#EDE0D0', bg4: '#E4D0BC',
      text: '#200E04', text2: '#583020', text3: '#9A6850',
      border: '#D8BEA8', border2: '#ECD8C8',
      sidebarFade: 'rgba(246,237,228,0.85)', sidebarFadeEnd: '#F6EDE4',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  rose: {
    // Happy Hues palette 12 — navy #232946 + rose #eebbc3 (periwinkle #b8c1ec)
    dark: {
      accent: '#EEBBC3', accentInk: '#232946',
      // Backgrounds — signature navy #232946 sits at bg3, extended darker/lighter
      bg: '#12162A', bg2: '#1A2038', bg3: '#232946', bg4: '#2E3557',
      // Text — periwinkle-tinted, paragraph #b8c1ec at text2
      text: '#EAECF8', text2: '#B8C1EC', text3: '#6C749C',
      border: '#2C3358', border2: '#39426A',
      sidebarFade: 'rgba(18,22,42,0.85)', sidebarFadeEnd: '#0D1020',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      // Pale rose #eebbc3 too light for white text → deepened to #C0446A (AA on white)
      accent: '#C0446A', accentInk: '#FFFFFF',
      bg: '#E6E8F5', bg2: '#F6F7FC', bg3: '#DCDFF2', bg4: '#CFD3EC',
      text: '#232946', text2: '#4A5178', text3: '#7C83A8',
      border: '#C6CAE6', border2: '#D8DBF0',
      sidebarFade: 'rgba(230,232,245,0.85)', sidebarFadeEnd: '#E6E8F5',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
  mono: {
    dark: {
      accent: '#D1D5DB', accentInk: '#111111',
      // Backgrounds — pure neutral
      bg: '#0E0F0E', bg2: '#161716', bg3: '#1E201E', bg4: '#262826',
      text: '#E6E8E6', text2: '#9A9C9A', text3: '#5C5E5C',
      border: '#2A2C2A', border2: '#333533',
      sidebarFade: 'rgba(14,15,14,0.85)', sidebarFadeEnd: '#0a0b0a',
      green: '#4ADE80', amber: '#FCD34D', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA',
    },
    light: {
      accent: '#374151', accentInk: '#FFFFFF',
      bg: '#F2F3F2', bg2: '#FAFAFA', bg3: '#EAECEC', bg4: '#E0E2E2',
      text: '#111312', text2: '#404442', text3: '#868A88',
      border: '#D0D4D2', border2: '#DCDEDE',
      sidebarFade: 'rgba(242,243,242,0.85)', sidebarFadeEnd: '#F2F3F2',
      green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', purple: '#7C3AED',
    },
  },
};

export function applyPaletteVars(key: PaletteKey, isDark: boolean): void {
  const t = PALETTES[key][isDark ? 'dark' : 'light'];
  const el = document.documentElement;
  el.setAttribute('data-theme', isDark ? 'dark' : 'light');
  el.style.colorScheme = isDark ? 'dark' : 'light';
  el.style.setProperty('--accent', t.accent);
  el.style.setProperty('--accent-ink', t.accentInk);
  el.style.setProperty('--bg', t.bg);
  el.style.setProperty('--bg2', t.bg2);
  el.style.setProperty('--bg3', t.bg3);
  el.style.setProperty('--bg4', t.bg4);
  el.style.setProperty('--text', t.text);
  el.style.setProperty('--text2', t.text2);
  el.style.setProperty('--text3', t.text3);
  el.style.setProperty('--border', t.border);
  el.style.setProperty('--border2', t.border2);
  el.style.setProperty('--sidebar-fade', t.sidebarFade);
  el.style.setProperty('--sidebar-fade-end', t.sidebarFadeEnd);
  el.style.setProperty('--green', t.green);
  el.style.setProperty('--amber', t.amber);
  el.style.setProperty('--red', t.red);
  el.style.setProperty('--blue', t.blue);
  el.style.setProperty('--purple', t.purple);

  // Android PWA status bar tracks the live palette/mode (Chrome re-reads this meta tag on
  // change). iOS ignores it — Safari's status bar only takes default/black/black-translucent,
  // no color param, so this has no effect there.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t.bg);
}

export function buildAntTheme(key: PaletteKey, isDark: boolean): ThemeConfig {
  const t = PALETTES[key][isDark ? 'dark' : 'light'];
  return {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: t.accent,
      colorBgBase: t.bg,
      colorBgContainer: t.bg2,
      colorBgElevated: t.bg3,
      colorBgLayout: t.bg,
      colorBorder: t.border,
      colorBorderSecondary: t.border2,
      colorText: t.text,
      colorTextSecondary: t.text2,
      colorTextTertiary: t.text3,
      colorSuccess: t.green,
      colorWarning: t.amber,
      colorError: t.red,
      colorInfo: t.blue,
      borderRadius: 8,
      borderRadiusLG: 12,
      fontFamily: "'Be Vietnam Pro', 'Inter', system-ui, sans-serif",
      fontSize: 14,
    },
    components: {
      Layout: { siderBg: t.bg2, headerBg: t.bg2, bodyBg: t.bg },
      Menu: isDark ? {
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemSelectedBg: 'transparent',
        darkItemSelectedColor: t.accent,
        darkItemHoverBg: t.bg3,
        darkItemHoverColor: t.text,
        darkGroupTitleColor: t.text3,
        itemHeight: 36,
        groupTitleFontSize: 10,
      } : {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemSelectedBg: 'transparent',
        itemSelectedColor: t.accent,
        itemHoverBg: t.bg3,
        itemHoverColor: t.text,
        groupTitleColor: t.text3,
        itemHeight: 36,
        groupTitleFontSize: 10,
      },
      Table: { headerBg: t.bg3, rowHoverBg: t.bg3 },
      Modal: { contentBg: t.bg3, headerBg: t.bg3 },
      Drawer: { colorBgElevated: t.bg3 },
      Card: { colorBgContainer: t.bg2 },
      Input: { colorBgContainer: t.bg3 },
      Select: { colorBgContainer: t.bg3 },
      DatePicker: { colorBgContainer: t.bg3 },
    },
  };
}
